import React, {useEffect, useRef} from 'react';
import {Pressable, ScrollView, useWindowDimensions, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialIcon from 'react-native-vector-icons/MaterialCommunityIcons';

import CCText from '../../../Atoms/CCText';
import {CourseChapter} from '../../../Hooks/LmsCourseApi/types';

import {CARD_SHADOW, F, MOBILE_BREAKPOINT} from '../courseTheme';

type Props = {
  chapters: CourseChapter[];
  currentIndex: number;
  completedPageIds: Set<number>;
  visible: boolean;
  onSelect: (index: number) => void;
  onClose: () => void;
};

const PANEL_WIDTH = 490;

/** Row height, used to scroll the list to the chapter in progress. */
const ROW_HEIGHT = 52;

/**
 * A generated course puts exactly one page in each chapter, and the player
 * renders `pages[0]` and nothing else. Judging "done" by every page would make
 * a chapter with two pages permanently unticked — the second page is
 * unreachable, so it can never be completed — so this reads the same page the
 * player actually shows.
 */
function isChapterDone(chapter: CourseChapter, completed: Set<number>) {
  const page = chapter?.pages?.[0];
  return !!page && completed.has(page.id);
}

/**
 * The chapter list, as a right-slide overlay rather than a persistent sidebar —
 * the shape the LMS player uses (nscl components/shared/SlideModal.tsx +
 * chapter/MissionListItem.tsx).
 *
 * A generated course runs to dozens of chapters, so Back/Continue alone leaves
 * a student with no way to reach the middle of it.
 */
export default function CourseChapterList({
  chapters,
  currentIndex,
  completedPageIds,
  visible,
  onSelect,
  onClose,
}: Props) {
  const {width} = useWindowDimensions();
  const scrollRef = useRef<ScrollView | null>(null);

  // Escape is the reflex for a slide-over on the web, and without it the only
  // exits are the ✕ and the scrim.
  useEffect(() => {
    if (!visible || typeof document === 'undefined') return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, onClose]);

  // Open at the chapter the student is on. A 57-chapter list that always opens
  // at row 1 means scrolling blind to find where you are.
  useEffect(() => {
    if (!visible) return;
    const y = Math.max(0, (currentIndex - 2) * ROW_HEIGHT);
    const id = setTimeout(() => scrollRef.current?.scrollTo({y, animated: false}), 0);
    return () => clearTimeout(id);
  }, [visible, currentIndex]);

  if (!visible) return null;

  const narrow = width < MOBILE_BREAKPOINT;

  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        flexDirection: 'row',
        justifyContent: 'flex-end',
      }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Close chapter list"
        onPress={onClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(38,35,34,0.6)',
        }}
      />

      <View
        style={[
          {
            width: narrow ? width : PANEL_WIDTH,
            backgroundColor: F.white,
            paddingHorizontal: narrow ? 16 : 40,
            paddingTop: 24,
            paddingBottom: 16,
          },
          CARD_SHADOW,
        ]}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 20,
          }}>
          <View>
            <CCText style={{fontSize: 22, fontWeight: '600', color: F.brownDark}}>
              Chapters
            </CCText>
            <CCText style={{fontSize: 14, fontWeight: '500', color: F.mutedText}}>
              {`${chapters.length} Chapters`}
            </CCText>
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={onClose}
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: '#FAF6EB',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Ionicons name="close" size={20} color={F.brown} />
          </Pressable>
        </View>

        <ScrollView>
          {chapters.map((chapter, i) => {
            const done = isChapterDone(chapter, completedPageIds);
            const active = i === currentIndex;
            const fill = done || active ? F.teal : 'transparent';
            // A generated course alternates video and puzzle, so the kind is
            // the single most useful thing to see when scanning 57 rows.
            const isPuzzle = chapter?.pages?.[0]?.content_type === 'chess_position';
            return (
              <Pressable
                accessibilityRole="button"
                key={chapter.id}
                accessibilityLabel={`Chapter ${chapter.chapter_number}`}
                accessibilityState={{selected: active, checked: done}}
                onPress={() => {
                  onSelect(i);
                  onClose();
                }}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                  paddingVertical: 10,
                  // The chapter in progress gets a highlight band, as the LMS
                  // list does. Without it, current and completed rows were the
                  // same teal disc distinguished only by ✓ versus a number.
                  ...(active
                    ? {
                        backgroundColor: '#FFF0C8',
                        marginHorizontal: narrow ? -16 : -40,
                        paddingHorizontal: narrow ? 16 : 40,
                      }
                    : null),
                }}>
                <View
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: fill,
                    borderWidth: fill === 'transparent' ? 1 : 0,
                    borderColor: F.muted,
                  }}>
                  <CCText
                    style={{
                      fontSize: 14,
                      fontWeight: '600',
                      color: fill === 'transparent' ? F.mutedText : F.white,
                    }}>
                    {done ? <Ionicons name="checkmark" size={16} color={F.white} /> : chapter.chapter_number}
                  </CCText>
                </View>
                <View
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 6,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isPuzzle ? 'rgba(225,120,70,0.14)' : F.badgeBg,
                  }}>
                  {/* MaterialCommunityIcons for the puzzle, Ionicons for the
                      play arrow. Not a style choice: Ionicons' glyph map JSON
                      lists `extension-puzzle`, but the font actually bundled
                      here is older and has no such codepoint, so every puzzle
                      row rendered a literal "?" — the same thing the drawer's
                      Opening Report row still does. `puzzle` is long-standing
                      in MaterialCommunityIcons and is already drawn in this
                      app's own left nav. */}
                  {isPuzzle ? (
                    <MaterialIcon name="puzzle" size={15} color={F.hintFg} />
                  ) : (
                    <Ionicons name="play" size={14} color={F.brown} />
                  )}
                </View>
                <CCText
                  // A generated title can be long; uncapped, one row grows to
                  // 90px and the list stops scanning evenly.
                  numberOfLines={2}
                  style={{
                    flex: 1,
                    fontSize: 16,
                    fontWeight: active ? '600' : '500',
                    color: active ? F.brownDark : F.brown,
                  }}>
                  {chapter.title}
                </CCText>
              </Pressable>
            );
          })}
        </ScrollView>
      </View>
    </View>
  );
}
